import { ModelDependency, ModelItem, ModelOption } from './model-item';
import { DefaultValueHandler, IModelContext } from './model.types';
import { ValidationFunction } from './validation/model-validation';

export enum ValueOptionsType {
  OneOf,
  ManyOf
}

export type ValueOptionsFilter = (modelContext: IModelContext, baseOptions?: ModelOption[]) => ModelOption[];

export type RequiredFunction = (modelContext: IModelContext) => boolean;

export class ValueOptions {
  type: ValueOptionsType = ValueOptionsType.OneOf;

  // über einen ValueOptionsFilter können die Options variabel zurückgegeben werden
  contextOptionsFilter: ValueOptionsFilter;

  noOptionsInfo = 'Es gibt keine Auswahloptionen!';

  constructor(public options?: ModelOption[]) {}

  withOptions(value: ModelOption[]): this {
    this.options = value;
    return this;
  }

  withType(type: ValueOptionsType): this {
    this.type = type;
    return this;
  }

  optionsForContext(modelContext: IModelContext): ModelOption[] {
    if (this.contextOptionsFilter) {
      return this.contextOptionsFilter(modelContext, this.options);
    } else {
      return this.options;
    }
  }

  withNoOptionsInfo(value: string): this {
    this.noOptionsInfo = value;
    return this;
  }

  get hasContextOptionsFilter(): boolean {
    return !!this.contextOptionsFilter;
  }

  nameStringForValues(values: any[]): string {
    return this.nameStringForValuesWithSeparator(null, values);
  }

  nameStringForValuesWithSeparator(separator = ', ', values: any[]): string {
    return this.namesForValues(values).join(separator);
  }

  namesForValues(values: any[]): string[] {
    return values.map((value: any) => this.options.find((option: ModelOption) => option.value === value).name);
  }
}

export type ReadOnlyFunction = (modelItem: ModelItem, modelContext: IModelContext) => boolean;

export class ModelItemOptions {
  private _dependencies: ModelDependency[] = [];
  readOnly: boolean;
  required: boolean;
  private _requiredFunction: RequiredFunction;
  readOnlyFunction: ReadOnlyFunction;
  maxlength: number;
  maxvalue: number;
  minvalue: number;
  minlength: number;
  removeDataWhenNotRelevant: boolean;
  noValidation: boolean;

  // Optional fuer "Auswahlfelder"
  valueOptions: ValueOptions;
  longListType: boolean;
  placeholder: string;

  defaultValueHandler: DefaultValueHandler;

  constructor(public _validations?: ValidationFunction[]) {}

  hasRequiredFunction(): boolean {
    return !!this._requiredFunction;
  }

  get requiredFunction(): RequiredFunction {
    return this._requiredFunction;
  }

  isRequired(modelContext: IModelContext): boolean {
    return this.hasRequiredFunction() && modelContext ? this.requiredFunction(modelContext) : this.required;
  }

  isWithRequiredHandling(): boolean {
    return this.required || !!this.requiredFunction;
  }

  withValidation(value: ValidationFunction): this {
    if (!this.validations.includes(value)) {
      this.validations.push(value);
    }
    return this;
  }

  withDefaultValueHandler(defaultValueHandler: DefaultValueHandler): this {
    this.defaultValueHandler = defaultValueHandler;
    return this;
  }

  withDependency(value: ModelDependency): this {
    this.dependencies.push(value);
    return this;
  }

  withReadOnly(value = true): this {
    this.readOnly = value;
    return this;
  }

  withRequired(value = true): this {
    this.required = value;
    return this;
  }

  withNoValidation(value = true): this {
    this.noValidation = value;
    return this;
  }

  withRequiredFunction(value: RequiredFunction): this {
    this._requiredFunction = value;
    return this;
  }

  withReadOnlyFunction(value: ReadOnlyFunction): this {
    this.readOnlyFunction = value;
    return this;
  }

  withValueOptions(value: ValueOptions, contextOptionsFilter?: ValueOptionsFilter): this {
    this.valueOptions = value;
    this.valueOptions.contextOptionsFilter = contextOptionsFilter;
    return this;
  }

  withLongListType(value: boolean): this {
    this.longListType = value;
    return this;
  }

  withPlaceholder(value: string): this {
    this.placeholder = value;
    return this;
  }

  withValueOptionsMany(value: ValueOptions, contextOptionsFilter?: ValueOptionsFilter): this {
    value.type = ValueOptionsType.ManyOf;
    return this.withValueOptions(value, contextOptionsFilter);
  }

  get validations(): ValidationFunction[] {
    if (!this._validations) {
      this._validations = [];
    }
    return this._validations;
  }

  get dependencies(): ModelDependency[] {
    if (!this._dependencies) {
      this._dependencies = [];
    }
    return this._dependencies;
  }

  addDependency(value: ModelDependency): this {
    this.dependencies.push(value);
    return this;
  }

  hasDependencies(): boolean {
    return this._dependencies && this._dependencies.length > 0;
  }

  hasValidations(): boolean {
    return this._validations && this._validations.length > 0;
  }
}
